import React from "react";
import { Shield, Wind, Compass, Waves, Heart, Sparkles } from "lucide-react";
import ApplicationTemplate from "@/components/applications/ApplicationTemplate";

const TraumaAdaptation = () => (
  <ApplicationTemplate
    pageKey="trauma-adaptation"
    breadcrumbLabel="Trauma Adaptation"
    overline="Application · Trauma Adaptation"
    headline="What is often called trauma"
    headlineAccent="is an intelligent adaptation the body made to survive."
    lead="Adaptive Physiology views trauma-related patterns as physiological adaptations worthy of respect — not defects to be corrected. The work is to gently offer the nervous system new information."
    loopHighlight="input"
    challenge={{
      title: "The body encoded a protective response. It kept running.",
      paragraphs: [
        "Under overwhelming, unpredictable, or inescapable input, the nervous system encodes protective adaptations — vigilance, disconnection, freeze responses, altered interoception. In the original context, these were competent responses.",
        "The challenge is not that the adaptations formed. The challenge is that they persist past the context that produced them — because the physiology has not received the information that the context has changed.",
      ],
    }}
    traditionalGap={{
      title: "Narrative alone cannot always reach a somatic adaptation.",
      paragraphs: [
        "Talk-based therapies are essential and, for many, life-changing — Adaptive Physiology does not stand in opposition to them. What we recognize is that trauma-related adaptations often live below the level of language.",
        "A protective physiology can be understood and still keep firing. Adaptive Physiology adds a physiological pathway — a slow, careful set of inputs the body can register directly.",
      ],
    }}
    perspective={{
      title: "Offer new information, gently and repeatedly.",
      paragraphs: [
        "Movement, rhythm, breath, environmental structure, and pacing are used to widen the window of tolerance and re-teach the system what safety physiologically feels like. Nothing is forced. Nothing is rushed.",
        "This work is done alongside — not instead of — the clinical care people already trust. Adaptive Physiology is a physiological layer added underneath a person’s existing support.",
      ],
    }}
    frameworkApplication={[
      {
        title: "Baseline with care",
        body: "Autonomic tone, sleep, interoceptive awareness, and current window of tolerance are read carefully. Nothing is measured that isn’t clearly consented to.",
      },
      {
        title: "Prescribe within the window",
        body: "Small, well-dosed inputs — breath, aerobic base, rhythm, environment — chosen to work inside the current tolerance rather than test it.",
      },
      {
        title: "Reinforce felt safety",
        body: "Repetition is what re-teaches the nervous system. Consistent, small doses of a regulated physiology accumulate into a new default.",
      },
      {
        title: "Retrain the reference point",
        body: "The window of tolerance widens. The protective adaptations remain available — they simply stop firing when they aren’t needed.",
      },
    ]}
    capacities={[
      { icon: Waves, label: "Window of tolerance", body: "A wider band inside which regulation remains available." },
      { icon: Wind, label: "Vagal access", body: "Physiological path back to a settled state." },
      { icon: Shield, label: "Contextual regulation", body: "The right response for the actual environment." },
      { icon: Compass, label: "Interoceptive clarity", body: "Body signals that can be read, not just endured." },
      { icon: Heart, label: "Felt safety", body: "A body that can register safety — not only detect threat." },
      { icon: Sparkles, label: "Adaptive flexibility", body: "New responses become physically available, not just conceptually." },
    ]}
  />
);
export default TraumaAdaptation;
